import BaseConfig from '@/config/base.js'
import SecretKey from '@/config/secret-key.js'
import * as Sentry from '@sentry/vue'

import { Toast } from 'vant'
import { decryptCode, getQueryParam, createDeviceId } from '@/utils/common.js'
import { parseUserInfoCookie } from '@/utils/cookie.js'
import { mockFlag, mockApp, appNameFlag, domainNameFlag } from '@/commonFunc/judge-package-name.js'

// 统一错误上报：控制台打印 + Sentry 上报
function reportError(err, context = {}) {
  console.log('try catch err:', err)
  try {
    Sentry.captureException(err, {
      extra: context,
      level: 'error',
    })
  } catch (_) {}
}

export default {
  // 立即执行 和global-mixin.js 中的env有区别
  env: (function () {
    try {
      const ua = navigator.userAgent
      return {
        // ua在app内 值是dq
        ua: ua,
        // app内可判断
        isApp: /dq/i.test(ua),
        isBrowser: !/dq/i.test(ua),
        // app外可判断
        isMobile: /android|iphone|ipod|ipad/i.test(ua),
        isIOS: /iphone|ipod|ipad|ios|mac os/i.test(ua),
        isAndroid: /android/i.test(ua),
      }
    } catch (err) {
      reportError(err, { method: 'env' })
      return {
        ua: '',
        isApp: false,
        isBrowser: true,
        isMobile: false,
        isIOS: false,
        isAndroid: false,
      }
    }
  })(),

  // 判断语言-链接
  getLangFromUrl(url) {
    try {
      if (url.indexOf('lang') > 0) {
        const queryLang = url.substring(url.indexOf('lang='), url.indexOf('lang=') + 7)
        const urlLang = queryLang.substr(queryLang.indexOf('=') + 1)
        return urlLang
      }
      return 'en'
    } catch (err) {
      reportError(err, { method: 'getLangFromUrl' })
      return 'en'
    }
  },

  // 获取包信息
  getAppInfo() {
    try {
      // app内
      if (this.env.isApp) {
        return parseUserInfoCookie() || {}
      }
      // 非app内
      else {
        let deviceId = ''
        let appPackageName = ''
        let channel = ''

        // 浏览器环境 仅为sayHi活动-浏览器登陆状态 使用
        if (this.env.isBrowser) {
          try {
            let da = getQueryParam(window.location.href, 'da')
            da = decodeURIComponent(da)
            let strObj = decryptCode(da, SecretKey.uploadCodePri)
            if (strObj) {
              strObj = JSON.parse(strObj)
            }
            if (strObj) {
              deviceId = strObj.deviceId
              appPackageName = strObj.appPackageName
            }

            // 为签名用(没有的话随机生成一个)
            if (!deviceId) {
              deviceId = createDeviceId()
            }
          } catch (err) {
            reportError(err, { method: 'getAppInfo.browser' })
          }
        }

        // localhsot-调试
        if (window.location.hostname === 'localhost') {
          deviceId = '9b7cdeac-9ea6-4467-b9c9-d94bdc690d4d'
          appPackageName = 'com.example.gcc'
          channel = 'vvg'
        }

        return {
          language: this.getLangFromUrl(window.location.hash),
          deviceId: deviceId,
          appPackageName: appPackageName,
          channel: channel,
        }
      }
    } catch (err) {
      reportError(err, { method: 'getAppInfo' })
      return {}
    }
  },

  // 获取用户id和token
  getUserBaseInfo() {
    try {
      // app内
      if (this.env.isApp) {
        const userInfo = parseUserInfoCookie()
        return {
          uid: userInfo?.uid || '',
          token: userInfo?.token || '',
        }
      }
      // 非app内
      else {
        // 好友
        // 1079 -> 2507

        let uid = ''
        let token = ''

        if (this.env.isBrowser) {
          try {
            let ut = getQueryParam(window.location.href, 'ut')
            ut = decodeURIComponent(ut)
            let strObj = decryptCode(ut, SecretKey.uploadCodePri)
            if (strObj) {
              strObj = JSON.parse(strObj)
            }
            if (strObj) {
              uid = strObj.uid
              token = strObj.token
            }
          } catch (err) {
            reportError(err, { method: 'getUserBaseInfo.browser' })
          }
        }

        // localhsot-调试代码
        // 2507 -> 41944 好友
        if (window.location.hostname === 'localhost') {
          // uid = '2507'
          // token = 'c281f3e7e17440ddae826017cddf0b1d'

          uid = '1079'
          token = 'd6bc332faac34aae8ec96284326831d1'
        }

        return {
          uid: uid,
          token: token,
        }
      }
    } catch (err) {
      reportError(err, { method: 'getUserBaseInfo' })
      return { uid: '', token: '' }
    }
  },

  // 获取包信息中的语言(优先取app设置的language 如果值为auto 则取系统language)
  getAppInfoLang() {
    try {
      // app内
      if (this.env.isApp) {
        const obj = parseUserInfoCookie()
        if (obj !== null && Object.keys(obj).length > 0) {
          if (obj.appLanguage) {
            return obj.appLanguage == 'auto' ? obj.language : obj.appLanguage
          }
          return obj.language
        }
      }
      // 非app内
      else {
        return this.getLangFromUrl(window.location.hash)
      }
    } catch (err) {
      reportError(err, { method: 'getAppInfoLang' })
      return 'en'
    }
  },

  // 获取包信息中的包名
  getPackageShortName() {
    let packageShortName = ''

    try {
      // app内
      if (this.env.isApp) {
        const appObj = parseUserInfoCookie()
        if (appObj !== null && Object.keys(appObj).length > 0) {
          if (appObj.appPackageName) {
            for (let i = 0; i < BaseConfig.validAppPackage.length + 1; i++) {
              const shortName = BaseConfig.validAppPackage[i]
              if (appNameFlag(shortName, appObj.appPackageName)) {
                packageShortName = shortName
                break
              }
            }
          }
        }
      }
      // 非app内
      else {
        for (let i = 0; i < BaseConfig.validAppPackage.length + 1; i++) {
          const shortName = BaseConfig.validAppPackage[i]
          if (domainNameFlag(shortName, window.location.hostname)) {
            packageShortName = shortName
            break
          }
        }
      }
      // localhsot-调试代码
      if (window.location.hostname === 'localhost') {
        packageShortName = mockFlag ? mockApp : 'vava'
      }
    } catch (err) {
      reportError(err, { method: 'getPackageShortName' })
    }

    return packageShortName
  },

  // 提示消息
  showToast(message) {
    const showVantToast = () => Toast({ message, duration: 3000 })
    try {
      if (!this.env.isApp) {
        showVantToast()
        return
      }
      const appInfo = this.getAppInfo()
      const useIosBridge = this.env.isIOS || String(appInfo.deviceType) === '1'
      const iosHandler = window.webkit?.messageHandlers?.showToast
      if (useIosBridge && iosHandler) {
        iosHandler.postMessage(message)
        return
      }
      const androidBridge = globalThis.showToast
      if (typeof androidBridge?.postMessage === 'function') {
        androidBridge.postMessage(message)
        return
      }
      showVantToast()
    } catch (err) {
      reportError(err, { method: 'showToast' })
      try {
        showVantToast()
      } catch (_) {}
    }
  },

  // 关闭webview
  closeActivity() {
    try {
      closeActivity.postMessage('')
    } catch (err) {
      reportError(err, { method: 'closeActivity' })
    }
  },

  // 关闭iOS页面
  closeActivityIOS() {
    try {
      window.webkit.messageHandlers.closeActivity.postMessage(null)
    } catch (err) {
      reportError(err, { method: 'closeActivityIOS' })
    }
  },

  // 给客户端传参数
  googleProductList(list, packageName) {
    try {
      // echo包通道
      if (packageName === 'echo') {
        queryProductList.postMessage(JSON.stringify(list))
      }
      // 其它包通道
      else {
        queryGoogleProductList.postMessage(JSON.stringify(list))
      }
    } catch (err) {
      reportError(err, { method: 'googleProductList' })
    }
  },

  // 拷贝链接
  copyLink(link, msg) {
    if (!link) return
    try {
      const input = document.createElement('input')
      input.value = link
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'fixed'
      input.style.top = '0'
      input.style.left = '0'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.focus()
      input.select()
      input.setSelectionRange(0, link.length)
      const ok = document.execCommand('copy')
      document.body.removeChild(input)
      if (ok && msg) {
        this.showToast(msg)
      }
    } catch (err) {
      reportError(err, { method: 'copyLink' })
    }
  },

  // 埋点
  addEvent(event, param) {
    try {
      if (this.env.isApp) {
        const params = {
          eventName: event,
          param: param,
        }
        addEvent.postMessage(JSON.stringify(params))
      }
    } catch (err) {
      reportError(err, { method: 'addEvent' })
    }
  },
}
